import { Fragment, useCallback, useEffect, useMemo } from "react"

import { useDispatch, useSelector } from "react-redux"

import { getCompareParams } from "../../Components/Filters/CompareSelect/presets"
import { FEATURE_FLAGS, isFeatureUnlocked } from "../../constants/feature-flags"
import { highAccessRoles } from "../../Utils/permission"
import { getDates } from "../../Utils/utils"
import { getDefaultDataSource } from "../Health/utils/getDefaultDataSource"
import { useDemoDataToggle } from "../User/hooks/useDemoDataToggle"
import AlertsRequiringAction from "./components/AlertsRequiringAction"
import DeflectedChart from "./components/DeflectedChart"
import DeflectLockedCard from "./components/DeflectLockedCard"
import HomeControls from "./components/HomeControls"
import KpiCards from "./components/KpiCards"
import PreventedChart from "./components/PreventedChart"
import RateChart from "./components/RateChart"
import { changeFilters, selectHomeFilters, selectHomeMainInfo } from "./redux/home.slice"
import {
  getHomeActionAlerts,
  getHomeDeflectedChart,
  getHomeKpiCards,
  getHomeMainInfo,
  getHomePreventedChart,
  getHomeRateChart
} from "./redux/home.thunks"

import styles from "./styles.module.less"

const HomePage = () => {
  const dispatch = useDispatch()
  const filters = useSelector(selectHomeFilters)
  const mainInfo = useSelector(selectHomeMainInfo)
  const userInfo = useSelector((state) => state.auth.userInfo)
  const businessAccount = useSelector((state) => state.user.businessAccount)
  const healthDataSource = useSelector((state) => state.health.dataSource)
  // Not a query param — the backend reads it off the user — but flipping it changes every answer,
  // so it rides in the memo to make each section's fetch callback re-run.
  const { isShowDemoData } = useDemoDataToggle()

  const businessAccountId = userInfo?.current_business_account_id

  // Home is open to every role, the Health endpoints are not; asking for a 403 on every period
  // change would only be noise, so those blocks skip the request and render their placeholder.
  const canReadHealth = !userInfo?.ba_role || highAccessRoles.includes(userInfo?.ba_role?.id)

  // Same gate as the sidebar's Receipt block: locked merchants get the placeholders instead of the
  // deflect numbers, and neither the chart nor the KPI card is asked for.
  const isDeflectLocked = !isFeatureUnlocked(userInfo, FEATURE_FLAGS.RECEIPTS)

  // Both choices are persisted and outlive a business-account switch, so neither counts until the
  // fetched account is the current one — otherwise Home would ask at the previous account's
  // processor — and neither counts once that processor is no longer connected.
  const connectedProcessors =
    businessAccount?.id === businessAccountId ? businessAccount?.connected_payment_processors : null

  const isConnected = (source) => Boolean(source) && Boolean(connectedProcessors?.includes(source))

  // What the page reads at until a card says otherwise. Health's own choice is the default, which
  // is what keeps UC6 true for a merchant arriving from there.
  const dataSource = isConnected(healthDataSource)
    ? healthDataSource
    : getDefaultDataSource(connectedProcessors ? businessAccount : null)

  // With no processor to read, Health answers every chart with flat zeroes and names the reason in
  // `warning_notification` — the chart says that instead of asking for the zeroes. Only once
  // main-info has answered: until then nothing is known, and the scope decides on its own.
  const healthWarning =
    mainInfo && !mainInfo.is_business_health_available ? mainInfo.warning_notification : null

  // The scope every block is read at, resolved once here so the sections all answer about the same
  // window. Memoised deliberately: it feeds the dependency list of the callbacks the sections call
  // from an effect, so a fresh object each render would refetch on every render.
  const query = useMemo(() => {
    const period = getDates(filters?.period)
    const compare = getCompareParams(filters?.compare, period)

    return {
      ready: Boolean(filters?.period),
      health: { ...period, ...compare, business_account: businessAccountId, source: dataSource },
      // Same account, spelled `business_account_id` here, and absent during onboarding.
      analytics: {
        ...period,
        ...compare,
        source: dataSource,
        ...(businessAccountId ? { business_account_id: businessAccountId } : {})
      },
      isShowDemoData
    }
  }, [filters?.period, filters?.compare, businessAccountId, dataSource, isShowDemoData])

  // Each section calls its own fetcher with what it owns — its `chart_type`, its paging — and the
  // page merges the scope in, so no section has to know how the window is spelled.
  const handleGetKpiCards = useCallback(
    (params) => {
      if (!query.ready) return
      dispatch(getHomeKpiCards({ ...query.analytics, ...params }))
    },
    [dispatch, query]
  )

  // Health's main-info is read for two fields only — whether Health has a processor, and why not —
  // which decide the rate card's banner. It is that card's own request, at that card's own
  // processor, so the card asks for it alongside its chart. The numbers on the cards come from the
  // KPI endpoint.
  const handleGetMainInfo = useCallback(
    (params) => {
      if (!query.ready || !canReadHealth) return
      dispatch(getHomeMainInfo({ ...query.health, ...params }))
    },
    [dispatch, query, canReadHealth]
  )

  const handleGetRateChart = useCallback(
    (params) => {
      if (!query.ready) return
      dispatch(getHomeRateChart({ ...query.health, ...params }))
    },
    [dispatch, query]
  )

  const handleGetPreventedChart = useCallback(
    (params) => {
      if (!query.ready) return
      dispatch(getHomePreventedChart({ ...query.analytics, ...params }))
    },
    [dispatch, query]
  )

  const handleGetDeflectedChart = useCallback(
    (params) => {
      if (!query.ready) return
      dispatch(getHomeDeflectedChart({ ...query.analytics, ...params }))
    },
    [dispatch, query]
  )

  // No period params: the controls row scopes the cards and the charts, not this table. `query` is
  // still a dependency for the demo toggle it carries, which the rule cannot see.
  const handleGetAlerts = useCallback(
    (params) => {
      dispatch(getHomeActionAlerts(params))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, query]
  )

  const handleChangeFilter = useCallback(
    (newFilters) => {
      if (Array.isArray(newFilters.period)) {
        dispatch(
          changeFilters({ ...newFilters, period: { type: "custom", period: newFilters.period } })
        )
      } else {
        dispatch(changeFilters(newFilters))
      }
    },
    [dispatch]
  )

  useEffect(() => {
    document.title = "Home - Chargeback"
  }, [])

  return (
    <div className={styles.content}>
      <HomeControls filters={filters} onChangeFilter={handleChangeFilter} />

      {/* A business-account switch is a different page of data, not an update to this one. Keying
          the blocks on the account remounts them, so every section refetches from its own mount
          effect rather than relying on each one keeping its fetch callback in its
          dependency array. */}
      <Fragment key={businessAccountId}>
        <KpiCards getCards={handleGetKpiCards} isDeflectLocked={isDeflectLocked} />

        <PreventedChart getChartData={handleGetPreventedChart} />

        {isDeflectLocked ? (
          <DeflectLockedCard />
        ) : (
          <DeflectedChart getChartData={handleGetDeflectedChart} />
        )}

        <RateChart
          canReadHealth={canReadHealth}
          healthWarning={healthWarning}
          getChartData={handleGetRateChart}
          getMainInfo={handleGetMainInfo}
          dataSource={dataSource}
        />

        <AlertsRequiringAction getAlerts={handleGetAlerts} />
      </Fragment>
    </div>
  )
}

export default HomePage
